import React, { useState, useMemo, ReactElement, useEffect } from 'react';

import classNames from 'classnames';

import { setFavorite } from '@/api/customer';
import useAccount from '@/store/account';
import useThrottle from '@/hooks/throttle';

import { WeeeIcon } from '@/components/common/Icon';
import useNeedLogin from '@/hooks/useNeedLogin';
import { appCollectStatusNotify } from '@/utils/weeeapp-notify';
import { BookmarkFilled, BookmarkOutline } from '@weee-fe/kunlunicon';
import { twMerge } from 'tailwind-merge';

export interface HeartProps {
  className?: string;
  id?: number;
  /** targetCollected 点击收藏按钮之后收藏返回true，点击之后取消收藏返回false*/
  onClick?: (targetCollected: boolean) => void;
  isLogin?: boolean;
  color?: string;
  isFavorite?: boolean;
  onSuccess?: (
    id: number | string,
    isFavorite?: boolean,
    supportPurchaseStatusNotify?: boolean,
    next?: () => void,
    fail?: () => void
  ) => void;
  render?: (
    node?: boolean,
    toggleCollect?: (e?: React.MouseEvent<HTMLElement>) => void,
    dom?: React.ReactNode
  ) => React.ReactNode;
  showToastInPDP?: boolean;
  supportPurchaseStatusNotify?: boolean;
  forceIconBlack?: boolean;
}

function Favorite({
  className,
  id,
  onSuccess,
  onClick,
  isFavorite,
  render,
  supportPurchaseStatusNotify,
  forceIconBlack
}: HeartProps) {
  const [collected, setCollected] = useState<boolean>(isFavorite || false);
  const { checkIsLogin } = useNeedLogin();

  const toggleCollect = useThrottle(() => {
    onClick?.(!collected);
    setCollected(collected => !collected);
    if (onSuccess) {
      return onSuccess?.(
        id,
        !isFavorite,
        supportPurchaseStatusNotify,
        () => {},
        () => {
          setCollected(collected => !collected);
        }
      );
    }
  }, 500);
  const handleClick = useThrottle(e => {
    e.stopPropagation();
    e.preventDefault();
    if (!checkIsLogin()) return;
    toggleCollect();
  }, 500);
  const defaultNode = (
    <button
      type="button"
      data-testid={collected ? 'btn-cancel-favorite' : 'btn-favorite'}
      aria-label={collected ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={collected}
      className={twMerge(
        'flex bg-transparent border-none outline-none appearance-none p-0 m-0 text-inherit',
        className
      )}
      style={{ display: 'flex' }}
      onClick={handleClick}>
      {collected ? (
        <BookmarkFilled className="font-normal text-xl text-pricing-bg" />
      ) : (
        <BookmarkOutline
          className={twMerge(forceIconBlack ? 'text-primary-5' : 'text-primary-1', 'font-normal text-xl')}
        />
      )}
    </button>
  );
  useEffect(() => {
    setCollected(isFavorite);
  }, [isFavorite]);
  if (render) {
    return render(collected, toggleCollect, defaultNode) as ReactElement<any>;
  }
  return defaultNode;
}

const FavoriteWrapper = ({ id, onClick, render, showToastInPDP, forceIconBlack, ...rest }: HeartProps) => {
  const { favoriteIds = [], syncFavoriteIds, showFavoriteTip, syncFavoriteClick } = useAccount();
  const isFavorite = useMemo(() => {
    return favoriteIds?.includes(+id);
  }, [id, favoriteIds?.join('_')]);
  const onSuccess = (id, collected, supportPurchaseStatusNotify = false, next, fail) => {
    let newIds = [...favoriteIds];
    if (collected) {
      newIds = newIds.concat(id);
      if (showToastInPDP) {
        showFavoriteTip(true);
        //3s后关闭
        setTimeout(() => {
          showFavoriteTip(false);
        }, 3000);
      }
    } else {
      newIds = favoriteIds.filter(item => item !== +id);
    }
    supportPurchaseStatusNotify && appCollectStatusNotify([id], collected ? 'add' : 'remove');

    setFavorite({
      type: 'product_favorite_a',
      target_id: id
    })
      .then(res => {
        syncFavoriteClick(true);
        if (res?.result) {
          // trackByFavoriteAdd(res.object);
          next();
          syncFavoriteIds(newIds);
        } else {
          fail?.();
          supportPurchaseStatusNotify && appCollectStatusNotify(favoriteIds, 'reset');
          syncFavoriteIds(favoriteIds);
        }
      })
      .catch(e => {
        fail?.();
        supportPurchaseStatusNotify && appCollectStatusNotify(favoriteIds, 'reset');
        syncFavoriteIds(favoriteIds);
      });
  };

  return (
    <Favorite
      id={id}
      onClick={onClick}
      isFavorite={isFavorite}
      forceIconBlack={forceIconBlack}
      {...rest}
      onSuccess={onSuccess}
      render={(id, toggle, defaultNode) => {
        const node = render ? render(id, toggle, defaultNode) : defaultNode;
        return node;
      }}
    />
  );
};

export default FavoriteWrapper;
